import { Link } from "react-router-dom";
import { useState } from "react";
import {
  UtensilsCrossed,
  CalendarDays,
  Users,
  Sparkles,
  Star,
  Briefcase,
  ExternalLink,
  ImageIcon,
  PartyPopper,
  MapPin,
  Phone,
  Instagram,
  Facebook,
  ArrowUpRight,
} from "lucide-react";
import { ASSETS, DOORDASH_URL } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import ReservationDialog from "@/components/site/ReservationDialog";
import HubReviewsTicker from "@/components/site/HubReviewsTicker";
import HubSubscribe from "@/components/site/HubSubscribe";
import MaintenancePage from "@/pages/MaintenancePage";
import PWABadge from "@/components/site/PWABadge";
import { BrunchBanner } from "@/components/site/BrunchBanner";

const TILES = [
  { label: "Menu", href: "/menu", icon: UtensilsCrossed, tone: "primary" },
  { label: "Reserve", href: "/reservations", icon: Users, tone: "text" },
  { label: "Order Online", href: "external", icon: ExternalLink, tone: "accent" },
  { label: "Specials", href: "/specials", icon: Sparkles, tone: "text" },
  { label: "Events", href: "/events", icon: CalendarDays, tone: "primary" },
  { label: "Gallery", href: "/gallery", icon: ImageIcon, tone: "text" },
  { label: "Reviews", href: "/reviews", icon: Star, tone: "accent" },
  { label: "Careers", href: "/careers", icon: Briefcase, tone: "text" },
];

const TONES = {
  primary: "bg-[var(--trio-primary)] text-white hover:bg-[var(--trio-primary-hover)] border-[var(--trio-primary)]",
  accent: "bg-[var(--trio-accent)] text-[var(--trio-text)] hover:bg-[var(--trio-primary)] hover:text-white border-[var(--trio-accent)]",
  text: "bg-[var(--trio-text)] text-[var(--trio-bg)] hover:bg-[var(--trio-primary)] border-[var(--trio-text)]",
};

export default function Landing() {
  const { address, phone, maintenance_mode } = useSettings();
  const [partyOpen, setPartyOpen] = useState(false);

  if (maintenance_mode) return <MaintenancePage />;

  return (
    <div
      data-testid="landing-hub"
      className="min-h-[100dvh] flex flex-col bg-[var(--trio-bg)] text-[var(--trio-text)] px-6 py-8 md:py-10 lg:py-12"
    >
      {/* Top row: logo + socials */}
      <div className="flex items-center justify-between max-w-6xl w-full mx-auto">
        <img src={ASSETS.logo} alt="Trio" className="h-16 sm:h-20 lg:h-24 w-auto" />
        <div className="flex items-center gap-2">
          <a
            href="https://www.instagram.com/triohsv/"
            target="_blank"
            rel="noreferrer"
            aria-label="Instagram"
            data-testid="hub-instagram"
            className="w-9 h-9 border border-[var(--trio-text)] hover:bg-[var(--trio-text)] hover:text-[var(--trio-bg)] flex items-center justify-center transition-colors"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <a
            href="https://www.facebook.com/share/17jHJeBudX/?mibextid=wwXIfr"
            target="_blank"
            rel="noreferrer"
            aria-label="Facebook"
            data-testid="hub-facebook"
            className="w-9 h-9 border border-[var(--trio-text)] hover:bg-[var(--trio-text)] hover:text-[var(--trio-bg)] flex items-center justify-center transition-colors"
          >
            <Facebook className="w-4 h-4" />
          </a>
        </div>
      </div>

      <BrunchBanner />

      {/* Headline */}
      <div className="flex-1 flex flex-col justify-center items-center text-center max-w-6xl w-full mx-auto py-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-3">
          Southern Soul Food · Bar & Lounge
        </div>
        <h1
          data-testid="hub-headline"
          className="font-display uppercase text-[52px] sm:text-[88px] lg:text-[120px] leading-[0.85] tracking-tight"
        >
          Eat. <span className="text-[var(--trio-primary)]">Drink.</span>{" "}
          <span className="text-[var(--trio-accent)]">Vibe.</span>
        </h1>

        {/* Tile grid */}
        <div
          data-testid="hub-tiles"
          className="mt-8 md:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-2.5 w-full max-w-4xl"
        >
          {TILES.map((t) => {
            const Icon = t.icon;
            const cls = TONES[t.tone];
            const inner = (
              <div className={`group aspect-square sm:aspect-[5/4] border ${cls} flex flex-col justify-between p-4 sm:p-5 transition-colors`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                <div className="flex items-end justify-between">
                  <span className="font-display uppercase text-lg sm:text-xl tracking-tight text-left leading-none">
                    {t.label}
                  </span>
                  <ArrowUpRight className="w-4 h-4 opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            );
            if (t.href === "external") {
              return (
                <a
                  key={t.label}
                  href={DOORDASH_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`hub-tile-${t.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  {inner}
                </a>
              );
            }
            return (
              <Link
                key={t.label}
                to={t.href}
                data-testid={`hub-tile-${t.label.toLowerCase().replace(/ /g, "-")}`}
              >
                {inner}
              </Link>
            );
          })}
        </div>

        {/* Party CTA */}
        <button
          onClick={() => setPartyOpen(true)}
          data-testid="hub-party-btn"
          className="mt-5 inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-widest border-b border-[var(--trio-text)] hover:text-[var(--trio-primary)] hover:border-[var(--trio-primary)] pb-1 transition-colors"
        >
          <PartyPopper className="w-4 h-4" /> Birthday or Group Party? Plan it here
        </button>

        <Link
          to="/reviews"
          data-testid="hub-review-discount"
          className="mt-3 inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-widest text-[var(--trio-accent)] hover:text-[var(--trio-primary)] font-bold"
        >
          <Star className="w-3.5 h-3.5 fill-[var(--trio-accent)]" />
          Leave a review · get a discount on your next visit
        </Link>

        {phone && (
          <a
            href={`tel:+1${phone.replace(/\D/g, "")}`}
            data-testid="hub-call-in"
            className="mt-4 inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2.5 text-xs sm:text-sm uppercase tracking-widest font-bold transition-colors animate-pulse hover:animate-none"
          >
            <Phone className="w-4 h-4" />
            Now Accepting Call-Ins · {phone}
          </a>
        )}

        <HubSubscribe />

        <HubReviewsTicker />
      </div>

      {/* Footer strip */}
      <div className="max-w-6xl w-full mx-auto flex flex-wrap items-center justify-between gap-4 text-xs text-[var(--trio-muted)] pt-6 border-t border-[var(--trio-border)]">
        <div className="flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 text-[var(--trio-primary)]" />
          <span>{address}</span>
        </div>
        <a
          href={phone ? `tel:+1${phone.replace(/\D/g, "")}` : "#"}
          className="flex items-center gap-2 hover:text-[var(--trio-primary)]"
        >
          <Phone className="w-3.5 h-3.5 text-[var(--trio-primary)]" />
          {phone}
        </a>
        <Link to="/staff/login" data-testid="landing-staff-login" className="uppercase tracking-widest hover:text-[var(--trio-primary)]">
          Employee Login
        </Link>
        <Link to="/admin/login" className="uppercase tracking-widest hover:text-[var(--trio-primary)]">
          Owner Login
        </Link>
      </div>

      <ReservationDialog open={partyOpen} onOpenChange={setPartyOpen} mode="group" />
      <PWABadge />
    </div>
  );
}
